/* KD9 — /app/perfil · Sistema Maestro de Identidad (Fase 1 y 2).
   Datos reales desde /api/me + edición vía PATCH /api/me/profile.
   Sin mocks. */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Mail, Phone, MapPin, Calendar, Shield, Edit2, Save, X,
  Award, Crown, Globe, User as UserIcon, ImagePlus, CheckCircle2, Sparkles,
} from "lucide-react";
import { KD9LogoCircle } from "@/components/KD9Logo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import WhatsAppVerification from "@/components/WhatsAppVerification";
import {
  PHONE_COUNTRIES,
  buildWhatsAppE164,
  countryByIso,
  nationalPhoneDigits,
  sanitizeNationalPhone,
  splitWhatsAppE164,
} from "@/lib/phoneCountries";

const ROLE_LABEL = {
  es: { user: "Usuario", student: "Estudiante", consultant: "Consultor", creator: "Creador", support: "Soporte KD9", admin: "Administrador KD9", director: "Director KD9" },
  en: { user: "User", student: "Student", consultant: "Consultant", creator: "Creator", support: "KD9 Support", admin: "KD9 Administrator", director: "KD9 Director" },
};

const ROLE_COLOR = {
  user: "#3C78B5",
  student: "#1FA774",
  consultant: "#A855F7",
  creator: "#A67C00",
  support: "#0E7A4D",
  admin: "#7A0E17",
  director: "#D4AF37",
};

const STATUS_LABEL = {
  es: { active: "Activa", suspended: "Suspendida", banned: "Bloqueada", pending: "Pendiente" },
  en: { active: "Active", suspended: "Suspended", banned: "Blocked", pending: "Pending" },
};

const CONSULTANT_SERVICE_LABELS = {
  quick_consultation: "Consulta Rápida",
  talk_with_consultant: "Hablar con un Consultor",
};

const fmtDate = (iso, lang) => iso ? new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "es-AR", { year: "numeric", month: "long", day: "2-digit" }) : "—";

const Perfil = () => {
  const { user: authUser, accessMap, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  const tr = (es, en) => lang === "en" ? en : es;
  const roleLabel = (role) => ROLE_LABEL[lang]?.[role] || role;
  const statusLabel = (status) => STATUS_LABEL[lang]?.[status] || status;
  const [user, setUser] = useState(authUser);
  const [loading, setLoading] = useState(!authUser);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [saving, setSaving] = useState(false);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState("");
  const [phoneCountryIso, setPhoneCountryIso] = useState("CO");
  const [phoneNational, setPhoneNational] = useState("");
  const [consultantPhotoData, setConsultantPhotoData] = useState("");
  const [consultantPhotoPreview, setConsultantPhotoPreview] = useState("");
  const [photoSaving, setPhotoSaving] = useState(false);
  const [avatarPhotoData, setAvatarPhotoData] = useState("");
  const [avatarPhotoPreview, setAvatarPhotoPreview] = useState("");
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [publicProfileSaving, setPublicProfileSaving] = useState(false);
  const [consultantProfile, setConsultantProfile] = useState({ first_name: "", specialty: "", bio: "", experience: "" });
  const [form, setForm] = useState({
    name: "", surname: "", phone: "", city: "", country: "", avatar_url: "",
    preferences: { language: "es", email_updates: true },
  });

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/api/me");
      setUser(data);
      const phoneParts = splitWhatsAppE164(data.phone || "");
      setPhoneCountryIso(phoneParts.countryIso);
      setPhoneNational(phoneParts.nationalNumber);
      setConsultantProfile({
        first_name: data.consultant_profile?.first_name || "",
        specialty: data.consultant_profile?.specialty || data.specialty || "",
        bio: data.consultant_profile?.bio || "",
        experience: data.consultant_profile?.experience || "",
      });
      setForm({
        name: data.name || "",
        surname: data.surname || "",
        phone: data.phone || "",
        city: data.city || "",
        country: data.country || "",
        avatar_url: data.avatar_url || "",
        preferences: {
          language: data.preferences?.language || "es",
          email_updates: data.preferences?.email_updates !== false,
        },
      });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const consultantSetupRequested = searchParams.get("consultantSetup") === "1";
  useEffect(() => {
    if (consultantSetupRequested && (authUser?.roles || []).includes("consultant")) {
      setEditMode(true);
    }
  }, [authUser, consultantSetupRequested]);

  const openEdit = () => { setEditMode(true); setOk(""); setError(""); setPhoneVerificationToken(""); };
  const cancelEdit = () => {
    setEditMode(false); setError(""); setOk("");
    // Reset form from user
    if (user) {
      const phoneParts = splitWhatsAppE164(user.phone || "");
      setPhoneCountryIso(phoneParts.countryIso);
      setPhoneNational(phoneParts.nationalNumber);
      setPhoneVerificationToken("");
      setForm({
        name: user.name || "",
        surname: user.surname || "",
        phone: user.phone || "",
        city: user.city || "",
        country: user.country || "",
        avatar_url: user.avatar_url || "",
        preferences: {
          language: user.preferences?.language || "es",
          email_updates: user.preferences?.email_updates !== false,
        },
      });
    }
  };

  const uploadAvatar = async () => {
    if (!avatarPhotoData) return;
    setAvatarSaving(true); setError(""); setOk("");
    try {
      const { data } = await api.post("/api/me/avatar", { photo_data: avatarPhotoData });
      setUser(data);
      setForm((current) => ({ ...current, avatar_url: data.avatar_url || "" }));
      setAvatarPhotoData("");
      setAvatarPhotoPreview("");
      setOk(tr("Foto de perfil actualizada correctamente.", "Profile photo updated successfully."));
      if (refresh) await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setAvatarSaving(false);
    }
  };

  const save = async () => {
    setSaving(true); setError(""); setOk("");
    try {
      const editedPhone = buildWhatsAppE164(phoneCountryIso, phoneNational);
      const nationalDigits = nationalPhoneDigits(phoneNational);
      if (editedPhone && (nationalDigits.length < 6 || editedPhone.replace(/\D/g, "").length > 15)) {
        setError(tr("Ingresa un número de WhatsApp válido para el país seleccionado.", "Enter a valid WhatsApp number for the selected country."));
        return;
      }
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim() || null,
        phone: editedPhone || null,
        whatsapp_verification_token: phoneVerificationToken || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        avatar_url: form.avatar_url.trim() || null,
        preferences: form.preferences,
      };
      const { data } = await api.patch("/api/me/profile", payload);
      setUser(data);
      setOk(tr("Perfil actualizado correctamente.", "Profile updated successfully."));
      setEditMode(false);
      if (refresh) await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="perfil-page" className="kd9-card p-10 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }}>
        {tr("Cargando perfil…", "Loading profile…")}
      </div>
    );
  }
  if (!user) {
    return (
      <div data-testid="perfil-page" className="kd9-card p-10 text-center font-mont text-sm" style={{ color: "#FCA5A5" }}>
        {error || tr("No se pudo cargar el perfil.", "The profile could not be loaded.")}
      </div>
    );
  }

  const roles = user.roles || ["user"];
  const isConsultant = roles.includes("consultant");
  const flags = user.flags || {};
  const isPremium = !!(flags.academy_premium);
  const isStudentActive = !!flags.student_active;
  const consultantSetup = accessMap?.consultant_setup || {};
  const consultantSetupRequired = isConsultant && Boolean(consultantSetup.required);
  const blockedConsultantServices = consultantSetup.blocked_capabilities || [];
  const consultantProfileMissing = consultantSetup.profile_missing || [];
  const fullName = [user.name, user.surname].filter(Boolean).join(" ").trim() || user.name;
  const selectedPhoneCountry = countryByIso(phoneCountryIso);
  const editedPhone = buildWhatsAppE164(phoneCountryIso, phoneNational);

  return (
    <div data-testid="perfil-page" className="grid lg:grid-cols-[360px_1fr] gap-6">
      {/* Left: identity card */}
      <aside className="kd9-card p-6 h-fit">
        <div className="relative mb-5">
          <div className="relative w-32 h-32 mx-auto">
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="w-32 h-32 rounded-full object-cover kd9-pulse"
                style={{ border: "2px solid var(--kd9-gold)" }}
                data-testid="profile-avatar-img"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="absolute inset-0 kd9-pulse" data-testid="profile-avatar-fallback">
                <KD9LogoCircle size={128} />
              </div>
            )}
          </div>
        </div>

        <h2 className="font-cinzel kd9-gold-text text-xl tracking-[0.06em] text-center break-words" data-testid="profile-fullname">
          {fullName.toUpperCase()}
        </h2>
        <div className="text-center text-[11px] font-mont tracking-widest uppercase mt-1 mb-4" style={{ color: "var(--kd9-text-soft)" }} data-testid="profile-primary-role">
          {roleLabel(roles[0])} · KD9
        </div>

        {/* Active roles chips */}
        <div className="flex flex-wrap gap-2 justify-center mb-5" data-testid="profile-roles-list">
          {roles.map((r) => {
            const color = ROLE_COLOR[r] || "#666";
            return (
              <span key={r} className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold"
                style={{ background: `${color}18`, color, border: `1px solid ${color}55` }}
                data-testid={`profile-role-chip-${r}`}>
                {roleLabel(r)}
              </span>
            );
          })}
          {isPremium && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold inline-flex items-center gap-1"
              style={{ background: "rgba(212,175,55,0.14)", color: "var(--kd9-gold)", border: "1px solid rgba(212,175,55,0.45)" }}
              data-testid="profile-premium-chip">
              <Crown size={10} /> Premium
            </span>
          )}
          {isStudentActive && !isPremium && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold"
              style={{ background: "rgba(31,167,116,0.14)", color: "#1FA774", border: "1px solid rgba(31,167,116,0.45)" }}
              data-testid="profile-student-active-chip">
              {tr("Estudiante activo", "Active student")}
            </span>
          )}
        </div>

        {/* Account status */}
        <div className="kd9-card p-3 mb-5 flex items-center gap-2" data-testid="profile-status"
          style={{ background: user.status === "active" ? "rgba(31,167,116,0.08)" : "rgba(122,14,23,0.10)", borderColor: user.status === "active" ? "rgba(31,167,116,0.35)" : "rgba(122,14,23,0.35)" }}>
          <CheckCircle2 size={14} style={{ color: user.status === "active" ? "#1FA774" : "#FCA5A5" }} />
          <div>
            <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>{tr("Estado de la cuenta", "Account status")}</div>
            <div className="font-mont text-sm font-semibold" style={{ color: user.status === "active" ? "#86EFAC" : "#FCA5A5" }}>{statusLabel(user.status)}</div>
          </div>
        </div>

        <ul className="space-y-2 text-sm font-mont">
          <li className="flex items-center gap-3" data-testid="profile-email"><Mail size={14} style={{ color: "var(--kd9-gold)" }} /><span className="truncate">{user.email}</span></li>
          {user.phone && <li className="flex items-center gap-3" data-testid="profile-phone"><Phone size={14} style={{ color: "var(--kd9-gold)" }} /><span>{user.phone} · {user.whatsapp_verified_at ? tr("WhatsApp verificado", "WhatsApp verified") : tr("WhatsApp sin verificar", "WhatsApp unverified")}</span></li>}
          {(user.city || user.country) && <li className="flex items-center gap-3" data-testid="profile-location"><MapPin size={14} style={{ color: "var(--kd9-gold)" }} /><span>{[user.city, user.country].filter(Boolean).join(", ")}</span></li>}
          {user.created_at && <li className="flex items-center gap-3" data-testid="profile-created"><Calendar size={14} style={{ color: "var(--kd9-gold)" }} /><span>{tr("Miembro desde", "Member since")} {fmtDate(user.created_at, lang)}</span></li>}
        </ul>

        {!editMode && (
          <button onClick={openEdit} className="kd9-btn-primary w-full py-3 rounded-lg text-[11px] mt-6 flex items-center justify-center gap-2" data-testid="profile-edit-btn">
            <Edit2 size={13} /> {tr("Editar perfil", "Edit profile")}
          </button>
        )}
      </aside>

      {/* Right: details + edit form */}
      <section className="space-y-6">
        {consultantSetupRequired && (
          <div
            className="kd9-card p-5"
            data-testid="consultant-activation-required"
            style={{
              borderColor: "rgba(245,158,11,.48)",
              background: "rgba(245,158,11,.06)",
            }}
          >
            <div className="flex items-start gap-3">
              <Shield size={19} style={{ color: "#FCD34D" }} className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-cinzel kd9-gold-text tracking-widest">
                  ACTIVACIÓN DE SERVICIOS PENDIENTE
                </div>
                <p className="font-cormorant text-lg mt-2" style={{ color: "var(--kd9-text-soft)" }}>
                  Dirección KD9 ya habilitó {blockedConsultantServices.map((key) => CONSULTANT_SERVICE_LABELS[key] || key).join(", ")} para tu cuenta.
                  Para que aparezcas a los usuarios debes completar personalmente tu información profesional.
                </p>
                <div className="mt-3 text-xs font-mont">
                  <span className="uppercase tracking-wider" style={{ color: "#FCD34D" }}>Pendiente:</span>{" "}
                  {consultantProfileMissing.join(", ") || "completar perfil profesional"}.
                </div>
                {!editMode && (
                  <button
                    type="button"
                    onClick={openEdit}
                    className="kd9-btn-primary mt-4 px-4 py-3 rounded-lg text-[10px]"
                    data-testid="consultant-activation-complete-now"
                  >
                    Completar información ahora
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {error && <div className="kd9-card p-4 text-sm font-mont" data-testid="profile-error"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}>{error}</div>}
        {ok && <div className="kd9-card p-4 text-sm font-mont" data-testid="profile-ok"
          style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)" }}>{ok}</div>}

        {editMode ? (
          <div className="kd9-card p-6" data-testid="profile-edit-form">
            <div className="flex items-center justify-between mb-4">
              <div className="font-cinzel kd9-gold-text text-lg tracking-widest">{tr("EDITAR PERFIL", "EDIT PROFILE")}</div>
              <button onClick={cancelEdit} className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-2" data-testid="profile-cancel-btn">
                <X size={12} /> {tr("Cancelar", "Cancel")}
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <Field label={tr("Nombre", "First name")} testid="profile-input-name">
                <input className="kd9-input pl-3 py-2 text-sm w-full" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="profile-input-name" />
              </Field>
              <Field label={tr("Apellidos", "Last name")} testid="profile-input-surname">
                <input className="kd9-input pl-3 py-2 text-sm w-full" value={form.surname} onChange={(e) => setForm((f) => ({ ...f, surname: e.target.value }))} data-testid="profile-input-surname" />
              </Field>
              <div className="md:col-span-2">
                <Field label={tr("WhatsApp", "WhatsApp")} testid="profile-input-phone">
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-2">
                    <select
                      className="kd9-input pl-3 pr-2 min-w-0"
                      value={phoneCountryIso}
                      onChange={(e) => {
                        setPhoneCountryIso(e.target.value);
                        setPhoneVerificationToken("");
                      }}
                      aria-label={tr("País de WhatsApp", "WhatsApp country")}
                      data-testid="profile-whatsapp-country"
                    >
                      {PHONE_COUNTRIES.map((country) => (
                        <option key={country.iso} value={country.iso}>
                          {country.name} ({country.dial})
                        </option>
                      ))}
                    </select>
                    <div className="relative min-w-0">
                      <span
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mont pointer-events-none"
                        style={{ color: "var(--kd9-gold)" }}
                        aria-hidden="true"
                      >
                        {selectedPhoneCountry.dial}
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        className="kd9-input min-w-0 w-full"
                        style={{ paddingLeft: `${Math.max(3.25, selectedPhoneCountry.dial.length * 0.72 + 1.7)}rem` }}
                        placeholder="300 000 0000"
                        value={phoneNational}
                        onChange={(e) => {
                          setPhoneNational(sanitizeNationalPhone(e.target.value));
                          setPhoneVerificationToken("");
                        }}
                        aria-describedby="profile-whatsapp-help"
                        data-testid="profile-input-phone"
                      />
                    </div>
                  </div>
                  <p id="profile-whatsapp-help" className="text-[11px] font-mont mt-2" style={{ color: "var(--kd9-text-soft)" }}>
                    {tr(
                      "Selecciona el país; KD9 agrega automáticamente el indicativo. Si cambias el número, deberás verificarlo antes de guardar.",
                      "Select the country; KD9 adds the calling code automatically. If you change the number, you must verify it before saving."
                    )}
                  </p>
                </Field>
              </div>
              {(editedPhone !== (user.phone || "").trim() || user.whatsapp_reverification_required || !user.whatsapp_verified_at) && (
                <div className="md:col-span-2">
                  <WhatsAppVerification phone={editedPhone} purpose="phone-change" onVerified={setPhoneVerificationToken} />
                </div>
              )}
              <Field label={tr("Ciudad", "City")} testid="profile-input-city">
                <input className="kd9-input pl-3 py-2 text-sm w-full" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="profile-input-city" />
              </Field>
              <Field label={tr("País", "Country")} testid="profile-input-country">
                <input className="kd9-input pl-3 py-2 text-sm w-full" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} data-testid="profile-input-country" />
              </Field>
              {!isConsultant && (
                <div className="md:col-span-2">
                  <Field label={tr("Foto de perfil", "Profile photo")} testid="profile-input-avatar">
                    <div className="kd9-card p-4 space-y-3">
                      <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0"
                          style={{ border: "2px solid var(--kd9-gold)", background: "var(--kd9-bg-2)" }}>
                          {(avatarPhotoPreview || user.avatar_url) ? (
                            <img
                              src={avatarPhotoPreview || user.avatar_url}
                              alt={tr("Vista previa de foto de perfil", "Profile photo preview")}
                              className="w-full h-full object-cover"
                              data-testid="profile-avatar-preview"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <UserIcon size={34} style={{ color: "var(--kd9-text-soft)" }} />
                            </div>
                          )}
                        </div>

                        <div className="w-full space-y-2">
                          <label
                            className="kd9-btn-outline px-4 py-3 rounded-lg text-[10px] cursor-pointer w-full flex items-center justify-center gap-2"
                            data-testid="profile-avatar-select"
                          >
                            <ImagePlus size={14} />
                            {tr("Seleccionar foto", "Choose photo")}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="sr-only"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 5 * 1024 * 1024) {
                                  setError(tr("La fotografía debe pesar máximo 5 MB.", "The photo must be 5 MB or smaller."));
                                  return;
                                }
                                const reader = new FileReader();
                                reader.onload = () => {
                                  const value = String(reader.result || "");
                                  setAvatarPhotoData(value);
                                  setAvatarPhotoPreview(value);
                                  setError("");
                                };
                                reader.readAsDataURL(file);
                              }}
                            />
                          </label>

                          <button
                            type="button"
                            onClick={uploadAvatar}
                            disabled={!avatarPhotoData || avatarSaving}
                            className="kd9-btn-primary px-4 py-3 rounded-lg text-[10px] w-full disabled:opacity-50"
                            data-testid="profile-avatar-upload"
                          >
                            {avatarSaving ? tr("Subiendo…", "Uploading…") : tr("Subir foto de perfil", "Upload profile photo")}
                          </button>

                          <p className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                            {tr("JPG, PNG o WebP. Máximo 5 MB.", "JPG, PNG or WebP. Maximum 5 MB.")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Field>
                </div>
              )}
              {isConsultant && <div className="md:col-span-2 kd9-card p-4 space-y-3">
                <div className="font-mont text-[10px] uppercase tracking-widest kd9-gold-text">Perfil público del Consultor</div>
                <p className="font-cormorant" style={{ color: "var(--kd9-text-soft)" }}>Dirección usa estos datos junto con la foto oficial aprobada para publicar tus servicios habilitados. Una capacidad puede quedar concedida pero no disponible al usuario mientras el perfil esté incompleto.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className="kd9-input" placeholder="Nombre de pila" value={consultantProfile.first_name} onChange={(e) => setConsultantProfile((p) => ({ ...p, first_name: e.target.value }))} />
                  <input className="kd9-input" placeholder="Especialidad profesional" value={consultantProfile.specialty} onChange={(e) => setConsultantProfile((p) => ({ ...p, specialty: e.target.value }))} />
                </div>
                <textarea className="kd9-input w-full min-h-28" placeholder="Descripción profesional pública (mínimo 40 caracteres)" value={consultantProfile.bio} onChange={(e) => setConsultantProfile((p) => ({ ...p, bio: e.target.value }))} />
                <textarea className="kd9-input w-full min-h-24" placeholder="Experiencia, formación y enfoque de atención" value={consultantProfile.experience} onChange={(e) => setConsultantProfile((p) => ({ ...p, experience: e.target.value }))} />
                <button type="button" disabled={publicProfileSaving} className="kd9-btn-primary px-4 py-3 rounded-lg text-[10px] disabled:opacity-50" onClick={async () => {
                  setPublicProfileSaving(true); setError(""); setOk("");
                  try {
                    const { data } = await api.put("/api/me/consultant-public-profile", consultantProfile);
                    setUser(data);
                    setOk("Perfil público profesional guardado. KD9 recalculará automáticamente la publicación de tus servicios.");
                    if (refresh) await refresh();
                    await reload();
                  } catch (e) { setError(formatApiError(e)); }
                  finally { setPublicProfileSaving(false); }
                }}>Guardar perfil profesional</button>
              </div>}
              {isConsultant && <div className="md:col-span-2 kd9-card p-4">
                <div className="font-mont text-[10px] uppercase tracking-widest kd9-gold-text mb-2">Actualizar fotografía oficial</div>
                <p className="font-cormorant mb-3" style={{ color: "var(--kd9-text-soft)" }}>La foto vigente no cambiará hasta que Dirección apruebe la nueva.</p>
                <div className="flex flex-wrap items-center gap-3">
                  {consultantPhotoPreview && <img src={consultantPhotoPreview} alt="Nueva fotografía frontal" className="w-24 h-24 rounded-xl object-cover" />}
                  <label className="kd9-btn-outline px-4 py-3 rounded-lg text-[10px] cursor-pointer">
                    Seleccionar foto frontal
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file || file.size > 5 * 1024 * 1024) { setError("La fotografía debe pesar máximo 5 MB."); return; }
                      const reader = new FileReader();
                      reader.onload = () => { setConsultantPhotoData(String(reader.result || "")); setConsultantPhotoPreview(String(reader.result || "")); setError(""); };
                      reader.readAsDataURL(file);
                    }} />
                  </label>
                  <button type="button" disabled={!consultantPhotoData || photoSaving} className="kd9-btn-primary px-4 py-3 rounded-lg text-[10px] disabled:opacity-50" onClick={async () => {
                    setPhotoSaving(true); setError(""); setOk("");
                    try {
                      await api.post("/api/me/consultant-photo", { photo_data: consultantPhotoData });
                      setConsultantPhotoData(""); setConsultantPhotoPreview("");
                      setOk("Fotografía enviada a revisión de Dirección. Tu foto actual permanece activa.");
                    } catch (e) { setError(formatApiError(e)); }
                    finally { setPhotoSaving(false); }
                  }}>Enviar a revisión</button>
                </div>
              </div>}
            </div>

            <div className="text-[10px] font-mont tracking-widest uppercase mb-2" style={{ color: "var(--kd9-text-soft)" }}>{tr("Preferencias personales", "Personal preferences")}</div>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <Field label={tr("Idioma preferido", "Preferred language")} testid="profile-input-pref-language">
                <select className="kd9-input pl-3 py-2 text-sm w-full" value={form.preferences.language} onChange={(e) => setForm((f) => ({ ...f, preferences: { ...f.preferences, language: e.target.value } }))} data-testid="profile-input-pref-language">
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </Field>
              <Field label={tr("Notificaciones por email", "Email notifications")} testid="profile-input-pref-email">
                <label className="flex items-center gap-3 mt-2 cursor-pointer">
                  <input type="checkbox" checked={form.preferences.email_updates} onChange={(e) => setForm((f) => ({ ...f, preferences: { ...f.preferences, email_updates: e.target.checked } }))} data-testid="profile-input-pref-email" />
                  <span className="font-mont text-sm">{form.preferences.email_updates ? tr("Activadas", "Enabled") : tr("Desactivadas", "Disabled")}</span>
                </label>
              </Field>
            </div>

            <button onClick={save} disabled={saving || !form.name.trim()} className="kd9-btn-primary w-full py-3 rounded-lg text-[11px] flex items-center justify-center gap-2"
              style={{ opacity: saving || !form.name.trim() ? 0.6 : 1 }} data-testid="profile-save-btn">
              <Save size={13} /> {saving ? tr("Guardando…", "Saving…") : tr("Guardar cambios", "Save changes")}
            </button>
          </div>
        ) : (
          <>
            {/* Datos personales */}
            <div className="kd9-card p-6" data-testid="profile-details">
              <div className="font-cinzel kd9-gold-text text-lg tracking-widest mb-4 flex items-center gap-2">
                <UserIcon size={16} /> {tr("DATOS PERSONALES", "PERSONAL INFORMATION")}
              </div>
              <dl className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                <Info label={tr("Nombre", "First name")} value={user.name} testid="profile-view-name" />
                <Info label={tr("Apellidos", "Last name")} value={user.surname} testid="profile-view-surname" />
                <Info label={tr("Correo electrónico", "Email address")} value={user.email} testid="profile-view-email" />
                <Info label={tr("Teléfono", "Phone")} value={user.phone} testid="profile-view-phone" />
                <Info label={tr("Ciudad", "City")} value={user.city} testid="profile-view-city" />
                <Info label={tr("País", "Country")} value={user.country} testid="profile-view-country" />
              </dl>
            </div>

            {/* Roles y estado */}
            <div className="kd9-card p-6" data-testid="profile-access">
              <div className="font-cinzel kd9-gold-text text-lg tracking-widest mb-4 flex items-center gap-2">
                <Shield size={16} /> {tr("ROLES Y ACCESO", "ROLES AND ACCESS")}
              </div>
              <dl className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                <Info label={tr("Rol principal", "Primary role")} value={roleLabel(user.role_base)} testid="profile-view-role-base" />
                <Info label={tr("Roles activos", "Active roles")} value={roles.map(roleLabel).join(", ")} testid="profile-view-roles" />
                <Info label={tr("Estado de la cuenta", "Account status")} value={statusLabel(user.status)} testid="profile-view-status" />
                <Info label={tr("Fecha de registro", "Registration date")} value={fmtDate(user.created_at, lang)} testid="profile-view-created" />
                {isPremium && <Info label={tr("Membresía Premium", "Premium membership")} value={tr("Activa", "Active")} testid="profile-view-premium" icon={<Crown size={12} className="kd9-gold-text" />} />}
              </dl>
            </div>

            {/* Preferencias */}
            <div className="kd9-card p-6" data-testid="profile-preferences">
              <div className="font-cinzel kd9-gold-text text-lg tracking-widest mb-4 flex items-center gap-2">
                <Sparkles size={16} /> {tr("PREFERENCIAS PERSONALES", "PERSONAL PREFERENCES")}
              </div>
              <dl className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                <Info label={tr("Idioma", "Language")} value={user.preferences?.language === "en" ? "English" : "Español"} testid="profile-view-pref-language" icon={<Globe size={12} />} />
                <Info label={tr("Notificaciones por email", "Email notifications")} value={user.preferences?.email_updates !== false ? tr("Activadas", "Enabled") : tr("Desactivadas", "Disabled")} testid="profile-view-pref-email" />
              </dl>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>{label}</div>
    {children}
  </div>
);

const Info = ({ label, value, testid, icon }) => (
  <div>
    <dt className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>{label}</dt>
    <dd className="font-mont text-sm flex items-center gap-2" data-testid={testid}>
      {icon}
      <span className="break-words">{value || "—"}</span>
    </dd>
  </div>
);

export default Perfil;
